import React, { useState, useRef } from "react";
import { Grid, Button, InputLabel, Avatar, TextField } from "@mui/material";
import organizationService from "services/organizationService";
import authService from "services/authService";
import PropTypes from "prop-types";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import unlockIcon from "assets/images/unlockIcon.svg";
import UserTable from "components/Tables/UsersTable";
import Loader from "components/Loader";
import noPicture from "components/Tables/no-picture.svg";
import axios from "axios";
import ImageCropperModal from "components/ImageCrop";
import tagAdminConstants from "constants/tagAdminConstants";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

function OrganizationDetailsForm({
  org,
  orgWithUser,
  handleCloseModal,
  openAlertModel,
  fetchAllApiData,
}) {
  const [openLoadingModal, setOpenLoadingModal] = useState(false);
  const [openCropModal, setOpenCropModal] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const hiddenFileInput = useRef(null);

  // Editable fields state
  const [formData, setFormData] = useState({
    email: org.email || "",
    address: org.address || "",
    details: org.details || "",
    logo: org.logo || "",
    logoPreview: "",
  });

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  // Open file input manually
  const handleLogoClick = () => {
    hiddenFileInput.current.click();
  };

  // Validate and open crop modal
  const imageValidation = (file) => {
    const valid = file.name.match(tagAdminConstants.UPLOAD_IMAGE_PATTERN_REGEX);
    return !valid; // returns true if invalid
  };

  const handleLogoChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const invalid = imageValidation(file);
    if (invalid) {
      openAlertModel(
        "organizationImageError",
        "Please select a valid image file.",
        "error",
        "organizationImageError"
      );
      return;
    }

    // open crop modal with selected image
    const reader = new FileReader();
    reader.onload = () => {
      setUploadedImage(reader.result);
      setOpenCropModal(true);
    };
    reader.readAsDataURL(file);
  };

  //  Called after user crops and saves
  const handleCropDone = ({ blob, fileUrl }) => {
    setFormData((prev) => ({
      ...prev,
      logo: blob,
      logoPreview: fileUrl,
    }));
    setOpenCropModal(false);
  };

  const handleQuillChange = (content) => {
    setFormData((prev) => ({ ...prev, details: content }));
  };

  // Update Organization API Call
  const handleUpdateOrganization = async () => {
    try {
      setOpenLoadingModal(true);

      const tokenInternal = localStorage.getItem("token");
      if (!tokenInternal) {
        openAlertModel(
          "organizationUpdateError",
          "No authorization token found.",
          "error",
          "organizationUpdateError"
        );
        setOpenLoadingModal(false);
        return;
      }

      const orgId =
        typeof org.id === "object"
          ? org.id.id || org.id.value || org.id.pk || org.id.uuid || ""
          : org.id;

      if (!orgId) {
        openAlertModel(
          "organizationUpdateError",
          "Invalid organization ID — cannot update.",
          "error",
          "organizationUpdateError"
        );
        setOpenLoadingModal(false);
        return;
      }

      const formDataToSend = new FormData();
      formDataToSend.append("email", formData.email);
      formDataToSend.append("address", formData.address);
      formDataToSend.append("details", formData.details);
      if (formData.logo instanceof File || formData.logo instanceof Blob) {
        formDataToSend.append("logo", formData.logo);
      }

      const headers = {
        Authorization: `Bearer ${tokenInternal}`,
      };

      const res = await axios.patch(
        `${BASE_URL}organizations/${orgId}/`,
        formDataToSend,
        { headers }
      );

      if (res.status === 200) {
        setFormData((prev) => ({
          ...prev,
          logo: res.data.logo || prev.logo,
          logoPreview: "",
        }));

        openAlertModel(
          "organizationUpdateSuccess",
          "Organization updated successfully!",
          "success",
          "organizationUpdateSuccess"
        );

        fetchAllApiData(org);
      } else {
        openAlertModel(
          "organizationUpdateError",
          "Failed to update organization. Please try again.",
          "error",
          "organizationUpdateError"
        );
      }
    } catch (error) {
      console.error("Update failed:", error);
      openAlertModel(
        "organizationUpdateError",
        error?.response?.data?.message ||
          "Error updating organization details.",
        "error",
        "organizationUpdateError"
      );
    } finally {
      setOpenLoadingModal(false);
    }
  };

  const handleActivateOrganization = (e, id, isActive) => {
    setOpenLoadingModal(true);
    organizationService
      .activateOrganization(e.target.value, id)
      .then(() => {
        setOpenLoadingModal(false);
        fetchAllApiData(org);
        openAlertModel(
          "organizationActivateSuccess",
          isActive
            ? "Organization Deactivated Successfully"
            : "Organization Activated Successfully",
          "success",
          "organizationActivateSuccess"
        );
      })
      .catch(() => {
        openAlertModel(
          "organizationActivateError",
          "An error occurred while activating the organization!",
          "error",
          "organizationActivateError"
        );
        setOpenLoadingModal(false);
      });
  };

  const handleBlockOrganization = (e, id, isBlocked) => {
    setOpenLoadingModal(true);
    organizationService
      .blockedOrganization(e.target.value, id)
      .then(() => {
        setOpenLoadingModal(false);
        fetchAllApiData(org);
        openAlertModel(
          "organizationBlockSuccess",
          isBlocked
            ? "Organization Unblocked Successfully"
            : "Organization Blocked Successfully",
          "success",
          "organizationBlockSuccess"
        );
      })
      .catch(() => {
        openAlertModel(
          "organizationBlockError",
          "An error occurred while blocking the organization!",
          "error",
          "organizationBlockError"
        );
        setOpenLoadingModal(false);
      });
  };

  const modules = {
    toolbar: [["bold", "italic"], ["link"], [{ list: "bullet" }]],
  };

  const lockUser = (id, val) => {
    authService
      .lockUser(id, val)
      .then(() => {
        openAlertModel(
          "lockUserAlertSuccess",
          "User was locked successfully!",
          "success",
          "lockUserAlertSuccess"
        );
      })
      .catch(() => {
        openAlertModel(
          "lockUserAlertError",
          "An error occurred while locking the user!",
          "error",
          "lockUserAlertError"
        );
      });
  };

  const deleteUser = (id) => {
    authService
      .deleteUser(id)
      .then(() => {
        openAlertModel(
          "deleteUserAlertSuccess",
          "User was deleted successfully!",
          "success",
          "deleteUserAlertSuccess"
        );
      })
      .catch(() => {
        openAlertModel(
          "deleteUserAlertError",
          "An error occurred while deleting the user!",
          "error",
          "deleteUserAlertError"
        );
      });
  };

  const resetPassword = (id) => {
    authService
      .resetUserPassword(id)
      .then(() => {
        openAlertModel(
          "resetPasswordAlertSuccess",
          "Password reset successfully!",
          "success",
          "resetPasswordAlertSuccess"
        );
      })
      .catch(() => {
        openAlertModel(
          "resetPasswordAlertError",
          "An error occurred while resetting the password!",
          "error",
          "resetPasswordAlertError"
        );
      });
  };

  return (
    <div className="b-organization-detail-container">
      <Loader title="Loading" openLoader={openLoadingModal} />

      {/* Logo Upload */}
      <Grid container alignItems="center" spacing={2}>
        <Grid item xs={12} md={12} className="organization-logo-section">
          <Avatar
            src={formData.logoPreview || formData.logo || noPicture}
            alt="Organization Logo"
            sx={{
              width: 120,
              height: 120,
              border: "1px solid #ccc",
              backgroundColor: "#fff",
              "& img": {
                objectFit: "contain",
                width: "100%",
                height: "100%",
              },
            }}
          />
        </Grid>

        <Grid item xs={12} md={9}>
          <Button
            variant="outlined"
            component="label"
            sx={{
              textTransform: "none",
              mt: { xs: 2, md: 0 },
            }}
            onClick={handleLogoClick}
          >
            Upload Logo
          </Button>
          <input
            type="file"
            hidden
            ref={hiddenFileInput}
            accept="image/*"
            onChange={handleLogoChange}
          />
        </Grid>
      </Grid>

      {/* Editable Fields */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <InputLabel className="font-bold">Primary Email</InputLabel>
          <TextField
            id="email"
            fullWidth
            variant="outlined"
            size="small"
            value={formData.email}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={12} md={1} />
        <Grid item xs={12} md={6}>
          <InputLabel className="font-bold">Address</InputLabel>
          <TextField
            id="address"
            fullWidth
            variant="outlined"
            size="small"
            multiline
            rows={2}
            value={formData.address}
            onChange={handleInputChange}
          />
        </Grid>
      </Grid>

      {/* Rich Text Editor */}
      <div style={{ marginTop: "1.5rem" }}>
        <InputLabel className="font-bold">Additional Details</InputLabel>
        <ReactQuill
          modules={modules}
          theme="snow"
          value={formData.details}
          onChange={handleQuillChange}
          style={{ backgroundColor: "#fff", minHeight: "150px" }}
        />
      </div>

      <UserTable
        HandleUserLock={lockUser}
        users={orgWithUser.userprofile_set}
        HandleUserDelete={deleteUser}
        HandleUserPasswordReset={resetPassword}
        handleCloseModal={handleCloseModal}
        openAlertModel={openAlertModel}
      />

      <Grid container className="b-organization-detail-container__btn-section">
        <Grid item xs={12} md={5.5}>
          {org.is_active === false ? (
            <Button
              className="b-organization-detail-container__active-btn"
              variant="outlined"
              value={1}
              onClick={(event) =>
                handleActivateOrganization(event, org.id, org.is_active)
              }
            >
              Activate Organization
            </Button>
          ) : (
            <Button
              className="b-organization-detail-container__block-btn"
              variant="outlined"
              value={0}
              onClick={(event) =>
                handleActivateOrganization(event, org.id, org.is_active)
              }
            >
              Deactivate Organization
            </Button>
          )}
        </Grid>
        <Grid item xs={1} />
        <Grid item xs={12} md={5.5}>
          {org.is_blocked === true ? (
            <Button
              className="b-organization-detail-container__active-btn"
              variant="outlined"
              value={0}
              onClick={(event) =>
                handleBlockOrganization(event, org.id, org.is_blocked)
              }
            >
              <span>
                <img
                  className="b-organization-detail-container__unlock-icon"
                  src={unlockIcon}
                  alt=""
                />
              </span>
              Unblock Organization
            </Button>
          ) : (
            <Button
              className="b-organization-detail-container__block-btn"
              variant="outlined"
              value={1}
              onClick={(event) => handleBlockOrganization(event, org.id)}
            >
              Block Organization
            </Button>
          )}
        </Grid>
      </Grid>

      <Grid
        container
        justifyContent="center"
        className="b-organization-detail-container__btn-section"
      >
        <Grid item xs={12} md={5.5} textAlign="center">
          <div className="btnlist">
            <Button
              variant="contained"
              onClick={handleUpdateOrganization}
              style={{
                fontSize: "1.125rem",
                backgroundColor: "#005298",
                color: "#fff",
                textTransform: "none",
                width: "100%",
              }}
            >
              Update
            </Button>
          </div>
        </Grid>
      </Grid>

      {/* Image Cropper Modal */}
      <ImageCropperModal
        open={openCropModal}
        onClose={() => setOpenCropModal(false)}
        imageSrc={uploadedImage}
        onCropDone={handleCropDone}
      />
    </div>
  );
}

OrganizationDetailsForm.propTypes = {
  org: PropTypes.oneOfType([PropTypes.object]).isRequired,
  orgWithUser: PropTypes.oneOfType([PropTypes.object]).isRequired,
  handleCloseModal: PropTypes.func.isRequired,
  openAlertModel: PropTypes.func.isRequired,
  fetchAllApiData: PropTypes.func.isRequired,
};

export default OrganizationDetailsForm;
